import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'

import { ConsumerScanListItem } from './list-item.component'
import { Dispatch, SetStateAction, useState } from 'react'
import { HelperWrapper } from 'src/components/helper-wrapper'
import { useItemsRemoveConfirm, useTableItemsCheck } from 'src/hooks'
import { IRewardActivityBaseWithId } from 'src/types/rewadr-acrivity.type'
import { useRemovePendingRewardActivities } from 'src/hooks/reward-activity/use-remove-pending-reward-acticities.hook'
import { DeclineInfoModal } from './decline-info-modal.component'
import { useGetDefaultRewardPoints } from 'src/hooks/reward-points'
import { ApprovalFeedbackModal } from './approval-feedback-modal.component'

interface IProps {
  data?: (IRewardActivityBaseWithId & {
    referralUser?: { id: string; email: string; name: string } | null
  })[]
  sort: string
  setSort: Dispatch<SetStateAction<string>>
}

export const ConsumerScansList = ({ data, sort, setSort }: IProps) => {
  const theme = useTheme()

  const isMobile = useMediaQuery(theme.breakpoints.down('laptop'))

  const [declineReason, setDeclineReason] = useState<null | string>(null)
  const [itemsId, setItemsId] = useState<null | string[]>(null)

  const { mutateAsync: removeItems } = useRemovePendingRewardActivities()

  const { data: defaultRewardPoints } = useGetDefaultRewardPoints()

  const {
    handleAllItemsCheck,
    selectedItemsIds,
    setSelectedItemsIds,
    isAllChecked,
    isIndeterminated,
    isAllUnchecked,
    ...itemsCheckProps
  } = useTableItemsCheck({
    items: data ?? [],
  })

  const { setRemoveConfirm } = useItemsRemoveConfirm()

  const handleItemsRemove = async (itemId?: string) => {
    if (!selectedItemsIds.length && !itemId) return

    setRemoveConfirm({
      handleConfirm: async () => {
        if (itemId) {
          removeItems([itemId])
        } else {
          await removeItems(selectedItemsIds)
        }
      },
      itemsTitles: [],
      itemsRelations: [],

      itemsType: selectedItemsIds.length > 1 ? 'reward activities' : 'reward activity',
    })

    setSelectedItemsIds([])
  }

  const handleSortChange = (sortName: string) => () => {
    setSort((prevSort) =>
      prevSort.split('-')[1] === 'asc' ? `${sortName}-desc` : `${sortName}-asc`,
    )
  }

  const activeSort = sort.split('-')[0]
  const sortDirection = sort.split('-')[1] as 'desc' | 'asc'

  const handleModalClose = () => {
    setDeclineReason(null)
  }

  const handleDeclineInfoModalOpen = (declineReason: string) => {
    setDeclineReason(declineReason)
  }

  const handleFeedbackModalClose = () => {
    setItemsId(null)
  }

  const handleFeedbackModalOpen = (ids: string[]) => {
    setItemsId(ids)
  }

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {!isMobile && (
                <TableCell sx={{ paddingX: '0px' }} align="center">
                  <IconButton
                    disabled={isAllUnchecked}
                    sx={{ ml: 'auto' }}
                    onClick={() => handleItemsRemove()}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              )}
              <TableCell sx={{ paddingX: '8px' }}>
                <TableSortLabel
                  onClick={handleSortChange('consumerScan.product_name')}
                  active={activeSort === 'consumerScan.product_name'}
                  direction={sortDirection}
                >
                  <HelperWrapper t="consumer-scan-history-product-column" />
                </TableSortLabel>
              </TableCell>
              {!isMobile && (
                <TableCell sx={{ paddingX: '0px' }} align="center">
                  <TableSortLabel
                    onClick={handleSortChange('rewardActivity.amount')}
                    active={activeSort === 'rewardActivity.amount'}
                    direction={sortDirection}
                    sx={{
                      marginLeft: '20px',
                    }}
                  >
                    <HelperWrapper t="consumer-scan-history-points-column" />
                  </TableSortLabel>
                </TableCell>
              )}
              {/* <TableCell align="center">Type</TableCell> */}
              <TableCell sx={{ paddingX: '8px' }} align="center">
                <TableSortLabel
                  onClick={handleSortChange('rewardActivity.status')}
                  active={activeSort === 'rewardActivity.status'}
                  direction={sortDirection}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="consumer-scan-history-status-column" />
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((item) => (
              <ConsumerScanListItem
                {...itemsCheckProps}
                item={item}
                key={item.id}
                handleDeclineInfoModalOpen={handleDeclineInfoModalOpen}
                handleFeedbackModalOpen={handleFeedbackModalOpen}
                rewardPointsInfo={defaultRewardPoints}
                handleItemRemove={handleItemsRemove}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <DeclineInfoModal
        declineReasonInfo={declineReason || ''}
        open={!!declineReason}
        handleClose={handleModalClose}
      />
      <ApprovalFeedbackModal
        itemsId={itemsId}
        open={!!itemsId}
        handleClose={handleFeedbackModalClose}
      />
    </>
  )
}
